import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { REPORT_MAX_RANGE_DAYS, REPORT_PRESETS, type ReportPreset } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { PageHeader } from '@/components/ui/PageHeader';
import { apiErrorCode } from '@/services/apiClient';
import { useOverviewQuery } from '../api/reports.api';
import { OverviewContent, OverviewSkeleton } from '../components/OverviewContent';
import { ReportFilterBar, type ReportFilters } from '../components/ReportFilterBar';
import { ReportMetaLine } from '../components/ReportMetaLine';
import { ReportTabs } from '../components/ReportTabs';
import { SmartReportsHero } from '../components/SmartReportsHero';

function readFilters(params: URLSearchParams): ReportFilters {
  const preset = params.get('preset');
  return {
    preset: REPORT_PRESETS.includes(preset as ReportPreset)
      ? (preset as ReportPreset)
      : 'THIS_MONTH',
    from: params.get('from') ?? '',
    to: params.get('to') ?? '',
    branchId: params.get('branch') ?? '',
    compare: params.get('compare') === 'none' ? 'none' : 'previous',
  };
}

/**
 * Reports › Overview. Filters live in the URL so a view can be bookmarked; they carry only
 * dates, a preset and a branch id — never a tenant, a user or any personal data.
 */
export function ReportsOverviewPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const filters = readFilters(params);
  const customReady = filters.preset !== 'CUSTOM' || (filters.from !== '' && filters.to !== '');

  const overview = useOverviewQuery(
    {
      preset: filters.preset,
      ...(filters.preset === 'CUSTOM' ? { from: filters.from, to: filters.to } : {}),
      branchId: filters.branchId || undefined,
      compare: filters.compare,
    },
    { enabled: customReady },
  );

  const update = (next: ReportFilters) => {
    const search = new URLSearchParams();
    search.set('preset', next.preset);
    if (next.preset === 'CUSTOM') {
      if (next.from) search.set('from', next.from);
      if (next.to) search.set('to', next.to);
    }
    if (next.branchId) search.set('branch', next.branchId);
    if (next.compare === 'none') search.set('compare', 'none');
    setParams(search, { replace: true });
  };

  return (
    <div className="space-y-5">
      <PageHeader title={t('reports.title')} subtitle={t('reports.subtitle')} />
      <ReportTabs />
      <SmartReportsHero />
      <ReportFilterBar value={filters} onChange={update} />
      {overview.data ? <ReportMetaLine meta={overview.data.meta} /> : null}
      {!customReady ? <Alert tone="info">{t('reports.filters.chooseDates')}</Alert> : null}
      {overview.isError ? (
        <Alert tone="error">
          {/* The server's own reason names a field in English; a refused period is the one case the reader can act on, so it gets a translated sentence that says what is allowed. */}
          {apiErrorCode(overview.error) === 'VALIDATION_FAILED'
            ? t('reports.invalidPeriod', { days: REPORT_MAX_RANGE_DAYS })
            : t('reports.loadError')}
        </Alert>
      ) : null}
      {overview.isPending && customReady ? <OverviewSkeleton /> : null}
      {overview.data ? <OverviewContent report={overview.data} /> : null}
    </div>
  );
}
